// Where the Gallery is: its home, one of its pages, and the picture viewer over them. Kept apart from the screens
// (GalleryPanel.tsx) so the way through it can be tested without a page (tests/gallery.test.ts).
import { signal } from '@preact/signals';
import { panel, selection, type Selection } from '../state';
import { openSizes } from './sizeBoards';

export interface GalleryViewer {
  /** The page and section the pictures come from; the viewer moves only within them. */
  page: string;
  section: string;
  items: string[];
  index: number;
  /** Where closing the viewer goes: back to the page under it, out of the Gallery (to a card), or back to Learn. */
  exit: 'page' | 'close' | 'learn';
}

export interface GalleryNav {
  /** The page open in the Gallery; null for its home. */
  page: string | null;
  /** A section of the page to show first (Saturn's rings from the home). */
  section?: string;
  viewer: GalleryViewer | null;
  /** The picture whose tile takes the focus back once the viewer closes. */
  returnTo?: string;
  /** Opened from a card in the sky (closing goes back to it, its "In the gallery" row focused), or from a world on
   * "How big are they?" (closing goes back there). */
  from?: 'card' | 'sizes';
  /** The world tapped on "How big are they?", to pick out again on going back there. */
  sizesBody?: string;
  /** Where the back button returns to, when this page was opened from another (a moon from its planet's page); else
   * it goes to the Gallery's home. */
  back?: GalleryBack;
  /** The card (a page's id) that takes the focus as the page opens: the one the reader came back from. */
  focus?: string;
}

/** The page another was opened from, the row on it, and the card in that row. */
export interface GalleryBack {
  page: string;
  section: string;
  card: string;
}

export const galleryNav = signal<GalleryNav>({ page: null, viewer: null });

/** Where the focus goes once the Gallery has handed back to the screen that opened it: a picture in Learn's row (its
 * id), or the "In the gallery" row of the card ('row'). Taken by that screen as it opens again, straight away. */
let returnFocus: { to: string; at: number } | null = null;
const handBack = (to: string) => void (returnFocus = { to, at: Date.now() });
export function takeReturnFocus(): string | null {
  const r = returnFocus;
  returnFocus = null;
  return r && Date.now() - r.at < 3000 ? r.to : null;
}

/** Open the Gallery: its home, or a page (and a section of it); `from` a card in the sky, closing returns to it. */
export function openGallery(page: string | null = null, section?: string, from?: 'card' | 'sizes', sizesBody?: string) {
  galleryNav.value = { page, section, viewer: null, ...(from ? { from } : {}), ...(sizesBody ? { sizesBody } : {}) };
  panel.value = 'gallery';
}

/** Move to another page of the open Gallery, or back to its home (null): at a section, with where it was opened from
 * (for its back button) or the card to focus. */
export function openPage(page: string | null, section?: string, how: { back?: GalleryBack; focus?: string } = {}) {
  const { from, sizesBody } = galleryNav.peek();
  galleryNav.value = { page, section, viewer: null, ...(from ? { from } : {}), ...(sizesBody ? { sizesBody } : {}), ...how };
}

/** Show pictures full screen, starting at `index`. */
export function openViewer(v: Omit<GalleryViewer, 'exit'> & { exit?: GalleryViewer['exit'] }) {
  if (!v.items.length) return;
  const index = Math.max(0, Math.min(v.items.length - 1, v.index));
  galleryNav.value = { ...galleryNav.peek(), viewer: { exit: 'page', ...v, index }, returnTo: undefined };
  if (panel.peek() !== 'gallery') panel.value = 'gallery';
}

/** The next (1) or previous (-1) picture of the section; false at either end. */
export function stepViewer(d: number): boolean {
  const nav = galleryNav.peek();
  const v = nav.viewer;
  if (!v) return false;
  const index = v.index + d;
  if (index < 0 || index >= v.items.length) return false;
  galleryNav.value = { ...nav, viewer: { ...v, index } };
  return true;
}

/** Close the viewer: back to the page under it (its tile takes the focus), or out to where it was opened from. */
export function closeViewer() {
  const nav = galleryNav.peek();
  const v = nav.viewer;
  if (!v) return;
  if (v.exit === 'page') {
    galleryNav.value = { ...nav, viewer: null, returnTo: v.items[v.index] };
    return;
  }
  galleryNav.value = { page: null, viewer: null };
  handBack(v.exit === 'learn' ? v.items[v.index] : 'row');
  panel.value = v.exit === 'learn' ? 'learn' : null;
}

/** Close the Gallery altogether. A card it was opened from shows again (the selection is kept), its row focused;
 * opened from "How big are they?", that page shows again where it was. */
export function closeGallery() {
  const { from, sizesBody } = galleryNav.peek();
  if (from === 'card') handBack('row');
  galleryNav.value = { page: null, viewer: null };
  if (panel.peek() !== 'gallery') return;
  if (from === 'sizes') openSizes(sizesBody);
  else panel.value = null;
}

/** Show the pictured object in the sky: select it, and leave the Gallery for its card. */
export function findInSky(sel: Selection) {
  galleryNav.value = { page: null, viewer: null };
  panel.value = null;
  selection.value = sel;
}
